import type { MouseEvent, ReactNode, Ref } from 'react';
import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import type { BreadcrumbLinkClickPayload } from '@noths/polaris-design-system-candidate-components';
import { Breadcrumbs } from '@noths/polaris-design-system-candidate-components';

import { OfferLabel } from 'src/components/atoms/OfferLabel/OfferLabel';
import { ProductBadge } from 'src/components/atoms/ProductBadge/ProductBadge';
import type { ProductFeaturesProps } from 'src/components/molecules/ProductFeatures/components/ProductFeatures';
import { ProductFeatures } from 'src/components/molecules/ProductFeatures/components/ProductFeatures';
import { ProductPrice } from 'src/components/molecules/ProductPrice/ProductPrice';
import { ProductRatingWithTracking } from 'src/components/molecules/ProductRating/ProductRatingWithTracking';
import type { PDPViewModel } from './types';
import * as styles from './PDPLayoutView.styles';

export interface PDPLayoutViewProps {
  /** Ref to the CTA grid cell, for drawers rendered outside the view. Only attaches when ctaTrigger is provided. */
  ctaColumnRef?: Ref<HTMLDivElement>;
  /** CTA trigger area (delivery summary, options trigger, basket CTA). Not an inline options section. */
  ctaTrigger?: ReactNode;
  linkCarousel?: ReactNode;
  /** Wider column, spans rows alongside the narrow-column content on desktop */
  media?: ReactNode;
  membershipBanner?: ReactNode;
  /** Called when a breadcrumb link is clicked; not called when viewModel.breadcrumbs is empty */
  onBreadcrumbLinkClick?: (
    e: MouseEvent<HTMLAnchorElement>,
    payload: BreadcrumbLinkClickPayload,
  ) => void;
  /** Full-width section with tinted background */
  partnerMaxiModule?: ReactNode;
  /** Partner mini module, sits in the narrow column below the CTA */
  partnerMiniModule?: ReactNode;
  partnerRecommendedProducts?: ReactNode;
  /** empty features = product features section hidden */
  productFeatures?: ProductFeaturesProps;
  productInformation?: ReactNode;
  publicAttributes?: ReactNode;
  recommendedProducts?: ReactNode;
  /** Slot content owns the reviews fragment id and its internal layout */
  reviews?: ReactNode;
  /** Rendered last; caller provides sticky wrapper and trigger wiring */
  stickyCta?: ReactNode;
  trustCarousel?: ReactNode;
  viewModel: PDPViewModel;
}

export const PDPLayoutView = ({
  ctaColumnRef,
  ctaTrigger,
  linkCarousel,
  media,
  membershipBanner,
  onBreadcrumbLinkClick,
  partnerMaxiModule,
  partnerMiniModule,
  partnerRecommendedProducts,
  productFeatures = { features: [] },
  productInformation,
  publicAttributes,
  recommendedProducts,
  reviews,
  stickyCta,
  trustCarousel,
  viewModel,
}: PDPLayoutViewProps) => {
  const showRating = Boolean(viewModel.productReviews.rating);

  return (
    <>
      <div css={visuallyHidden} role="status">
        {viewModel.pageLiveRegionText}
      </div>
      <div css={[styles.maxWidthContainer, styles.grid]}>
        {viewModel.breadcrumbs.length > 0 && (
          <div css={styles.breadcrumbsArea}>
            <Breadcrumbs links={viewModel.breadcrumbs} onLinkClick={onBreadcrumbLinkClick} />
          </div>
        )}
        <div css={styles.imagesArea}>{media}</div>
        <div css={styles.titleArea}>
          <h1 css={[styles.productTitle]}>{viewModel.title}</h1>
          <div
            css={styles.descriptionArea}
            dangerouslySetInnerHTML={{ __html: viewModel.description }}
          />
          <section css={styles.priceRow}>
            <ProductPrice
              preSalePrices={viewModel.price.preSalePrices}
              price={viewModel.price.price}
              priceIsMinimum={viewModel.price.priceIsMinimum}
              salePercentage={viewModel.price.salePercentage}
              salePercentageInRed={viewModel.price.salePercentageInRed}
            />
            {(viewModel.badge || showRating) && (
              <div css={styles.ratingPill}>
                <ProductRatingWithTracking
                  averageRating={viewModel.productReviews.rating ?? undefined}
                  numberOfReviews={viewModel.productReviews.count}
                  reviewsLinkHref={viewModel.productReviews.href}
                />
                {viewModel.badge && <ProductBadge badge={viewModel.badge} />}
              </div>
            )}
          </section>
          {viewModel.offerLabel && (
            <div css={styles.offerLabel}>
              <OfferLabel {...viewModel.offerLabel} />
            </div>
          )}
          {ctaTrigger && (
            <div css={styles.ctaTriggerStack} ref={ctaColumnRef}>
              {ctaTrigger}
            </div>
          )}
          {partnerMiniModule}
          {productFeatures.features.length > 0 && (
            <ProductFeatures
              features={productFeatures.features}
              onVisible={productFeatures.onVisible}
            />
          )}
        </div>
      </div>
      <div css={[styles.maxWidthContainer, styles.column]}>
        {reviews}
        <div css={styles.productDetailsWrapper}>
          {productInformation && (
            <div css={styles.productInformationArea}>{productInformation}</div>
          )}
          {publicAttributes && <div css={styles.publicAttributesArea}>{publicAttributes}</div>}
        </div>
        {partnerMaxiModule}
        {partnerRecommendedProducts}
        {recommendedProducts}
        {trustCarousel}
        {membershipBanner}
        {linkCarousel}
        {stickyCta}
      </div>
    </>
  );
};
